function validDateMs(value) {
  if (!value) return Number.POSITIVE_INFINITY;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

function finiteDateMs(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function sortEdgesByDate(edges = []) {
  return [...edges].sort((a, b) => validDateMs(a.dateMet) - validDateMs(b.dateMet));
}

export function buildAdjacency(people = [], edges = []) {
  const adjacency = new Map(people.map((person) => [person._id, new Set()]));
  edges.forEach((edge) => {
    if (!edge?.person1 || !edge?.person2) return;
    if (!adjacency.has(edge.person1)) adjacency.set(edge.person1, new Set());
    if (!adjacency.has(edge.person2)) adjacency.set(edge.person2, new Set());
    adjacency.get(edge.person1).add(edge.person2);
    adjacency.get(edge.person2).add(edge.person1);
  });
  return adjacency;
}

export function shortestPath(people = [], edges = [], startId, endId) {
  if (!startId || !endId) return [];
  if (startId === endId) return [startId];

  const adjacency = buildAdjacency(people, edges);
  const queue = [startId];
  const previous = new Map([[startId, null]]);

  while (queue.length) {
    const current = queue.shift();
    for (const next of adjacency.get(current) || []) {
      if (previous.has(next)) continue;
      previous.set(next, current);
      if (next === endId) {
        const path = [endId];
        let cursor = current;
        while (cursor) {
          path.unshift(cursor);
          cursor = previous.get(cursor);
        }
        return path;
      }
      queue.push(next);
    }
  }

  return [];
}

function bfsDistances(adjacency, startId, ignoredId) {
  const distances = new Map([[startId, 0]]);
  const queue = [startId];

  while (queue.length) {
    const current = queue.shift();
    for (const next of adjacency.get(current) || []) {
      if (next === ignoredId || distances.has(next)) continue;
      distances.set(next, distances.get(current) + 1);
      queue.push(next);
    }
  }

  return distances;
}

function buildRecruitment(people = [], edges = []) {
  const sortedEdges = sortEdgesByDate(edges);
  const joined = new Set();
  const introducedCounts = new Map();
  const children = new Map(people.map((person) => [person._id, []]));

  sortedEdges.forEach((edge) => {
    const a = edge.person1;
    const b = edge.person2;
    const aJoined = joined.has(a);
    const bJoined = joined.has(b);

    if (aJoined && !bJoined) {
      introducedCounts.set(a, (introducedCounts.get(a) || 0) + 1);
      children.get(a)?.push(b);
    } else if (bJoined && !aJoined) {
      introducedCounts.set(b, (introducedCounts.get(b) || 0) + 1);
      children.get(b)?.push(a);
    }

    joined.add(a);
    joined.add(b);
  });

  const descendantCount = (personId, seen = new Set()) => {
    if (seen.has(personId)) return 0;
    seen.add(personId);
    return (children.get(personId) || []).reduce((sum, childId) => (
      sum + 1 + descendantCount(childId, seen)
    ), 0);
  };

  const chains = people.map((person) => ({
    personId: person._id,
    count: descendantCount(person._id),
  })).sort((a, b) => b.count - a.count);

  return { introducedCounts, chains };
}

function connectedComponents(people = [], edges = []) {
  const adjacency = buildAdjacency(people, edges);
  const visited = new Set();
  const components = [];

  people.forEach((person) => {
    if (visited.has(person._id)) return;
    const component = [];
    const queue = [person._id];
    visited.add(person._id);

    while (queue.length) {
      const current = queue.shift();
      component.push(current);
      for (const next of adjacency.get(current) || []) {
        if (visited.has(next)) continue;
        visited.add(next);
        queue.push(next);
      }
    }

    components.push(component);
  });

  return components;
}

function findWorstBreakupCouple(people = [], edges = []) {
  const datingEdges = edges.filter((edge) => edge?.category === 'dating');
  if (!datingEdges.length) return null;

  const baseComponentCount = connectedComponents(people, edges).length;
  const adjacency = buildAdjacency(people, edges);

  return datingEdges.reduce((best, edge) => {
    const remainingEdges = edges.filter((candidate) => candidate._id !== edge._id);
    const components = connectedComponents(people, remainingEdges);
    const componentByPersonId = new Map();
    components.forEach((component, componentIndex) => {
      component.forEach((personId) => componentByPersonId.set(personId, componentIndex));
    });

    const firstComponentIndex = componentByPersonId.get(edge.person1);
    const secondComponentIndex = componentByPersonId.get(edge.person2);
    const isSeparated = firstComponentIndex !== secondComponentIndex;
    const firstGroupSize = components[firstComponentIndex]?.length || 0;
    const secondGroupSize = components[secondComponentIndex]?.length || 0;
    const componentIncrease = Math.max(0, components.length - baseComponentCount);
    const combinedDegree = (adjacency.get(edge.person1)?.size || 0) + (adjacency.get(edge.person2)?.size || 0);
    const score = (componentIncrease * 10000)
      + (isSeparated ? 5000 : 0)
      + (Math.min(firstGroupSize, secondGroupSize) * 100)
      + combinedDegree;
    const candidate = {
      edge,
      score,
      componentCount: components.length,
      componentIncrease,
      separatedGroupSizes: isSeparated ? [firstGroupSize, secondGroupSize].sort((a, b) => b - a) : [],
      combinedDegree,
    };

    return !best || candidate.score > best.score ? candidate : best;
  }, null);
}

function analyzeFamily(people = [], edges = []) {
  const childEdges = edges.filter((edge) => edge?.category === 'child');
  const siblingEdges = edges.filter((edge) => edge?.category === 'sibling');
  const partnerEdges = edges.filter((edge) => edge?.category === 'partner' || edge?.category === 'married');
  const childConnectionCounts = new Map();

  childEdges.forEach((edge) => {
    childConnectionCounts.set(edge.person1, (childConnectionCounts.get(edge.person1) || 0) + 1);
    childConnectionCounts.set(edge.person2, (childConnectionCounts.get(edge.person2) || 0) + 1);
  });

  const oldestPerson = [...people]
    .filter((person) => finiteDateMs(person.birthDate) !== null)
    .sort((a, b) => finiteDateMs(a.birthDate) - finiteDateMs(b.birthDate))[0] || null;

  const longestLife = [...people]
    .map((person) => {
      const birth = finiteDateMs(person.birthDate);
      if (birth === null) return null;
      const death = finiteDateMs(person.deathDate) || Date.now();
      return {
        personId: person._id,
        years: Math.max(0, (death - birth) / (1000 * 60 * 60 * 24 * 365.25)),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.years - a.years)[0] || null;

  const mostChildConnections = [...childConnectionCounts.entries()]
    .map(([personId, count]) => ({ personId, count }))
    .sort((a, b) => b.count - a.count)[0] || null;

  return {
    oldestPerson,
    longestLife,
    mostChildConnections,
    oldestMarriage: sortEdgesByDate(partnerEdges.filter((edge) => edge.category === 'married'))[0] || partnerEdges[0],
    mostRecentSiblingBond: [...sortEdgesByDate(siblingEdges)].reverse()[0],
    childEdgeCount: childEdges.length,
    siblingEdgeCount: siblingEdges.length,
    partnerEdgeCount: partnerEdges.length,
  };
}

function mostCentralForEdges(people = [], edges = []) {
  const adjacency = buildAdjacency(people, edges);
  return people.reduce((best, person) => {
    const distances = bfsDistances(adjacency, person._id);
    let sum = 0;
    let reachable = 0;
    distances.forEach((distance, otherId) => {
      if (otherId === person._id || distance <= 0) return;
      sum += distance;
      reachable += 1;
    });
    const score = reachable ? reachable / sum : 0;
    const candidate = {personId: person._id, score, averageDistance: reachable ? sum / reachable : 0};
    return !best || candidate.score > best.score ? candidate : best;
  }, null);
}

function analyzeWedding(people = [], edges = []) {
  const weddingPartyEdges = edges.filter((edge) => edge?.category === 'wedding_party');
  const coupleEdge = sortEdgesByDate(edges.filter((edge) => edge?.category === 'married'))[0];
  const guestEdges = edges.filter((edge) => edge?.category === 'guest');
  const familyEdges = edges.filter((edge) => edge?.category === 'family');
  return {
    coupleEdge,
    weddingPartyMvp: weddingPartyEdges.length ? mostCentralForEdges(people, weddingPartyEdges) : null,
    mostConnectedGuest: guestEdges.length ? mostCentralForEdges(people, guestEdges) : null,
    oldestFamilyBond: sortEdgesByDate(familyEdges)[0],
    weddingPartyCount: new Set(weddingPartyEdges.flatMap((edge) => [edge.person1, edge.person2])).size,
  };
}

function articulationPoints(people = [], adjacency) {
  let time = 0;
  const visited = new Set();
  const disc = new Map();
  const low = new Map();
  const parent = new Map();
  const points = new Set();

  const dfs = (id) => {
    visited.add(id);
    disc.set(id, ++time);
    low.set(id, disc.get(id));
    let children = 0;

    for (const next of adjacency.get(id) || []) {
      if (!visited.has(next)) {
        children += 1;
        parent.set(next, id);
        dfs(next);
        low.set(id, Math.min(low.get(id), low.get(next)));
        if (!parent.has(id) && children > 1) points.add(id);
        if (parent.has(id) && low.get(next) >= disc.get(id)) points.add(id);
      } else if (next !== parent.get(id)) {
        low.set(id, Math.min(low.get(id), disc.get(next)));
      }
    }
  };

  people.forEach((person) => {
    if (!visited.has(person._id)) dfs(person._id);
  });

  return [...points];
}

export function analyzeGraph(people = [], edges = []) {
  const peopleById = new Map(people.map((person) => [person._id, person]));
  const sortedEdges = sortEdgesByDate(edges);
  const adjacency = buildAdjacency(people, edges);
  const degreeDistribution = new Map();
  let distanceTotal = 0;
  let pairCount = 0;
  let central = null;

  people.forEach((person) => {
    const distances = bfsDistances(adjacency, person._id);
    let sum = 0;
    let reachable = 0;

    distances.forEach((distance, otherId) => {
      if (otherId === person._id || distance <= 0) return;
      sum += distance;
      reachable += 1;
      if (person._id < otherId) {
        degreeDistribution.set(distance, (degreeDistribution.get(distance) || 0) + 1);
        distanceTotal += distance;
        pairCount += 1;
      }
    });

    const score = reachable ? reachable / sum : 0;
    if (!central || score > central.score) {
      central = { personId: person._id, score, averageDistance: reachable ? sum / reachable : 0 };
    }
  });

  const { introducedCounts, chains } = buildRecruitment(people, sortedEdges);
  const topIntroducers = [...introducedCounts.entries()]
    .map(([personId, count]) => ({ personId, count }))
    .sort((a, b) => b.count - a.count);
  const hiddenConnectorIds = articulationPoints(people, adjacency);

  return {
    sortedEdges,
    averageDistance: pairCount ? distanceTotal / pairCount : 0,
    degreeDistribution: [...degreeDistribution.entries()].sort((a, b) => a[0] - b[0]),
    mostCentral: central,
    oldestFriendship: sortedEdges.find((edge) => Number.isFinite(validDateMs(edge.dateMet))) || sortedEdges[0],
    oldestNonFamilyBond: sortedEdges.find((edge) => edge?.category !== 'family' && Number.isFinite(validDateMs(edge.dateMet)))
      || sortedEdges.find((edge) => edge?.category !== 'family'),
    mostRecentFriendship: [...sortedEdges].reverse().find((edge) => Number.isFinite(validDateMs(edge.dateMet))) || sortedEdges[sortedEdges.length - 1],
    worstBreakupCouple: findWorstBreakupCouple(people, sortedEdges),
    mostInfluential: topIntroducers[0],
    topIntroducers,
    largestRecruitmentChain: chains[0],
    hiddenConnectors: hiddenConnectorIds.map((id) => ({
      personId: id,
      degree: adjacency.get(id)?.size || 0,
    })).sort((a, b) => a.degree - b.degree),
    family: analyzeFamily(people, sortedEdges),
    wedding: analyzeWedding(people, sortedEdges),
    peopleById,
  };
}

export function simulateRemoval(people = [], edges = [], personId) {
  if (!personId) return null;
  const remainingPeople = people.filter((person) => person._id !== personId);
  const remainingEdges = edges.filter((edge) => edge.person1 !== personId && edge.person2 !== personId);
  const adjacency = buildAdjacency(remainingPeople, remainingEdges);
  const visited = new Set();
  const components = [];

  remainingPeople.forEach((person) => {
    if (visited.has(person._id)) return;
    const distances = bfsDistances(adjacency, person._id, personId);
    const component = [...distances.keys()];
    component.forEach((id) => visited.add(id));
    components.push(component);
  });

  components.sort((a, b) => b.length - a.length);
  return {
    componentCount: components.length,
    largestComponentSize: components[0]?.length || 0,
    isolatedGroups: components.filter((component) => component.length <= 2).length,
    components,
  };
}
